import { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import {
  BlockOutlined,
  CancelOutlined,
  ErrorOutline,
  EventBusyOutlined,
  GroupsOutlined,
  HourglassEmptyOutlined,
  TouchAppOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { useTitle } from "utils/UseTitle";
import { getErrorStatusCode, handleErrorMessage, openErrorNotification } from "helpers/methods";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  beginInPersonSession,
  endInPersonSession,
  selectInPersonSession,
} from "redux/inPersonSessionSlice";
import {
  InPersonSessionStatus,
  InPersonSignerSummary,
} from "models/interfaces";
import { getSignRequestData } from "screens/MySignRequests/MySignRequestService";
import {
  fetchInPersonHostLink,
  fetchInPersonSessionStatus,
  returnInPersonHostLinkSession,
  returnInPersonSession,
  startInPersonHostLinkSession,
  startInPersonSession,
} from "./InPersonSigningService";
import {
  getDisplaySignerEmail,
  isInPersonSessionSigner,
} from "./InPersonSigningHelper";
import InPersonHandoffDialog from "./InPersonHandoffDialog";
import inPersonStyles from "./InPersonSigning.styles";

/** What either data source (token GET or authed envelope fetch) boils down to. */
interface LandingInfo {
  requestId: string;
  emailSubject: string;
  requestedByName: string;
  /**
   * The server's own readiness answer — who may be handed the device right
   * now, who the envelope is waiting on, and whether a session may start at
   * all. This page renders that verbatim rather than working it out from the
   * envelope's signer list: the list it used to filter has no notion of the
   * signing order the hand-off actually honours, so on a level shared by an
   * in-person and an email-link signer it offered a hand-off the server could
   * only refuse — and, once the in-person half had signed, kept offering one
   * while the emailed signer was still out.
   */
  session: InPersonSessionStatus;
}

/**
 * Older backends answer the host-link GET without the `session` block. Rebuild
 * the minimum from the flat fields so the page still works: `canStartSession`
 * from the pending count, and no picker (the flat shape carries no per-signer
 * detail, which is exactly what the block was added to provide).
 */
const sessionFromFlatFields = (response: {
  requestId?: string;
  status?: string;
  pendingInPersonSigners?: number;
  sessionInProgress?: boolean;
  sessionHeldByAnotherHost?: boolean;
}): InPersonSessionStatus => ({
  requestId: response.requestId,
  status: response.status,
  actionableSigners: [],
  waitingOn: [],
  sessionInProgress: response.sessionInProgress,
  sessionHeldByAnotherHost: response.sessionHeldByAnotherHost,
  canStartSession:
    (response.pendingInPersonSigners ?? 0) > 0 &&
    !response.sessionHeldByAnotherHost,
});

/** Name for a picker row / waiting chip, falling back the way the row lists do. */
const summaryLabel = (summary: InPersonSignerSummary): string =>
  summary.name?.trim() ||
  getDisplaySignerEmail(summary.signerEmail).trim() ||
  strings.inPerson.unnamedSignerLabel;

/**
 * The one in-person launch page, reached two ways that end up in the exact
 * same place:
 *  - the public link mailed to `signRequestConfig.inPersonHostEmail`
 *    (`/in-person/host?token=`) — no login, the token is the sole credential.
 *  - "Sign in person" on the view-request page (`/in-person/host?id=`) — the
 *    logged-in requester/sender, navigated here instead of popping a dialog
 *    on that page, so both hosts go through identical UI and copy.
 *
 * Only the credential differs past this point: token mode posts to the
 * `*HostLink*` endpoints, id mode to the host-auth ones. `InPersonSessionShell`
 * carries that same split forward for the rest of the session (next/return).
 */
const InPersonHostLinkScreen = () => {
  useTitle(title.IN_PERSON_HOST);
  const theme = useTheme();
  const styles = inPersonStyles(theme);
  const dispatch = useAppDispatch();
  const existingSession = useAppSelector(selectInPersonSession);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";
  // The signed key bound to `token` -- see DocumentSignHelper.inPersonHostLinkUrl.
  const key = params.get("key") ?? "";
  const requestId = params.get("id") ?? "";
  const isTokenMode = Boolean(token);

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<LandingInfo | null>(null);
  const [loadError, setLoadError] = useState<string>("");
  const [captureOpen, setCaptureOpen] = useState<boolean>(false);
  const [captureMsg, setCaptureMsg] = useState<string>("");
  const [actionBusy, setActionBusy] = useState<boolean>(false);
  // Which actionable signer the host chose, by `signerRef`. Only meaningful
  // when the server reports more than one; a lone signer needs no choosing and
  // a placeholder has no ref to choose by (the server auto-picks for those).
  const [selectedRef, setSelectedRef] = useState<string>("");

  useEffect(() => {
    if (!token && !requestId) {
      setLoading(false);
      setLoadError(strings.inPerson.hostLinkLoadFailed);
      return;
    }
    let stale = false;
    setLoading(true);

    const load: Promise<LandingInfo> = token
      ? fetchInPersonHostLink(token, key).then((response) => ({
          requestId: response.requestId,
          emailSubject: response.emailSubject,
          requestedByName: response.requestedByName,
          // The link's landing payload already embeds the same readiness block
          // the authed endpoint returns, so token mode needs no second call.
          session: response.session ?? sessionFromFlatFields(response),
        }))
      : // The status endpoint is the authority on readiness but says nothing
        // about the envelope itself, and the ready card names the sender and
        // subject — so both, in parallel.
        Promise.all([
          getSignRequestData(requestId),
          fetchInPersonSessionStatus(requestId),
        ]).then(([signRequest, session]: [any, InPersonSessionStatus]) => ({
          requestId: String(session?.requestId ?? signRequest?.id ?? requestId),
          emailSubject: signRequest?.emailSubject ?? "",
          requestedByName: signRequest?.requestedBy?.name ?? "",
          session: session ?? {},
        }));

    load
      .then((info) => {
        if (stale) return;
        setData(info);
        setLoadError("");
      })
      .catch((error: any) => {
        if (stale) return;
        setLoadError(error?.message || strings.inPerson.hostLinkLoadFailed);
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [token, key, requestId]);

  const session = data?.session;
  const actionableSigners = session?.actionableSigners ?? [];
  const waitingOn = session?.waitingOn ?? [];
  // A single actionable signer is the choice; past that the host has to make
  // one, and a signer with no `signerRef` cannot be targeted at all (the
  // server auto-picks those), so they are never pre-selected.
  const chosenSigner =
    actionableSigners.length === 1
      ? actionableSigners[0]
      : actionableSigners.find((signer) => signer.signerRef === selectedRef);
  const needsChoice = actionableSigners.length > 1;
  const canContinue = Boolean(session?.canStartSession) &&
    (!needsChoice || Boolean(chosenSigner));

  // A different envelope's session left active on this device (host hit back
  // instead of "Return to host") would be silently overwritten by starting a
  // new one, orphaning its backend lock until the server times it out.
  // Released right before the real start call, not on the first card's
  // button — that one makes no API call at all.
  const releasePreviousSession = async () => {
    if (!existingSession.active) return;
    try {
      if (existingSession.hostToken) {
        await returnInPersonHostLinkSession(
          existingSession.hostToken,
          existingSession.hostKey,
          existingSession.signerRef,
          true,
        );
      } else if (existingSession.requestId) {
        await returnInPersonSession(
          existingSession.requestId,
          existingSession.signerRef,
          true,
        );
      }
    } catch (error: any) {
      console.error("in-person: releasing the previous session failed", error);
    }
    dispatch(endInPersonSession());
  };

  const handleActionError = (error: any) => {
    const code = getErrorStatusCode(error);
    if (code === 401) {
      // Token mode: the link itself has gone bad mid-flow (e.g. expired
      // between the GET and this call). Id mode: the login session expired —
      // either way there's nothing more this page can do on its own.
      setData(null);
      setLoadError(error?.message || strings.inPerson.hostLinkLoadFailed);
    } else if (code === 409) {
      // Non-retryable: another host holds this envelope. The server message
      // names them, so prefer it over generic copy.
      openErrorNotification(error?.message || strings.inPerson.sessionInProgress);
    } else if (code === 423) {
      openErrorNotification(error?.message || strings.inPerson.lockedByAdmin);
    } else if (code === 403) {
      openErrorNotification(error?.message || strings.inPerson.launchForbidden);
    } else {
      handleErrorMessage(error);
    }
  };

  const enterSession = (response: any) => {
    dispatch(
      beginInPersonSession({
        requestId: data?.requestId ?? "",
        response,
        hostToken: isTokenMode ? token : undefined,
        hostKey: isTokenMode ? key : undefined,
      }),
    );
    window.location.assign(
      `${viewpaths.doSign}?id=${encodeURIComponent(
        response.requestToken,
      )}&key=${encodeURIComponent(response.requestKey || "")}&inperson=1`,
    );
  };

  /**
   * Collapses the page to the not-ready card. Used when a start call comes
   * back with nothing to hand off — or with the wrong signer — so the host
   * sees why instead of a stale "ready to start".
   */
  const markNotReady = (reason?: string) =>
    setData((prev) =>
      prev
        ? {
            ...prev,
            session: {
              ...prev.session,
              actionableSigners: [],
              canStartSession: false,
              waitingReason: reason || prev.session.waitingReason,
            },
          }
        : prev,
    );

  // The only card that starts a session — name + optional email + the host's
  // consent, all in one, whichever mode this page is in.
  const handleCaptureSubmit = async (name: string, email: string) => {
    setActionBusy(true);
    try {
      await releasePreviousSession();
      // Target the chosen signer explicitly when the host had a choice to
      // make; omit it otherwise and let the server auto-pick, which is also
      // the only option for a placeholder with no ref of its own.
      const options = {
        name,
        email,
        signerEmail: chosenSigner?.signerRef || undefined,
      };
      const response = isTokenMode
        ? await startInPersonHostLinkSession(token, key, options)
        : await startInPersonSession(data!.requestId, options);
      // Same refusal `InPersonSessionShell.runNext` makes: a session that
      // resolved to an emailed signer must not open their document here, so
      // it collapses to the not-ready card instead of a hand-off.
      const wrongSigner = Boolean(
        response?.requestToken && !isInPersonSessionSigner(response),
      );
      if (wrongSigner) {
        console.error(
          "in-person: session/start resolved to a non in-person signer",
          response?.signerRef,
        );
      }
      if (wrongSigner || response?.sessionComplete || !response?.requestToken) {
        setActionBusy(false);
        setCaptureOpen(false);
        markNotReady();
        return;
      }
      // Deliberately left open and busy, not closed — `enterSession` hard-
      // navigates away, and that isn't instant. Closing the dialog here would
      // un-hide the stale "ready to start" card underneath (see the
      // `!captureOpen &&` guard below) for the beat before the browser
      // actually leaves. The whole page is about to be torn down anyway, so
      // there's nothing to reset once we get here.
      enterSession(response);
    } catch (error: any) {
      setActionBusy(false);
      const code = getErrorStatusCode(error);
      // Keep the dialog open on a repeated validation failure (still a blank
      // placeholder, wrong OTP-required email, ...) so the host can correct
      // it; bail to the normal handler for anything else.
      if (code === 406) {
        setCaptureMsg(error?.message || "");
      } else {
        setCaptureOpen(false);
        handleActionError(error);
      }
    }
  };

  const renderCard = (
    icon: ReactNode,
    iconSx: SxProps<Theme>,
    heading: string,
    body: string,
    action?: ReactNode,
  ) => (
    <Box sx={styles.overlay}>
      <Box sx={styles.card}>
        <Box sx={iconSx}>{icon}</Box>
        {/* Each card title is the page heading (WCAG 1.3.1). */}
        <Typography component="h1" sx={styles.title}>{heading}</Typography>
        <Typography sx={styles.subtitle}>{body}</Typography>
        {action && <Box sx={styles.actions}>{action}</Box>}
      </Box>
    </Box>
  );

  /**
   * The host reopening a link whose envelope already reached a terminal
   * state — same outcomes (and copy, adapted for this page) as the banner a
   * logged-in requester sees on the view-request page
   * (`ViewRequestStatusMeta.getBannerConfig`). `null` for anything still in
   * progress, so the ready-to-start / not-ready cards still apply.
   */
  const getTerminalCard = (status?: string) => {
    switch (status) {
      case strings.completed:
      case strings.signed:
      case strings.approved:
        return renderCard(
          <VerifiedOutlined />,
          styles.iconCircleSuccess,
          strings.inPerson.requestCompletedTitle,
          strings.inPerson.requestCompletedSubtitle,
        );
      case strings.voided:
        return renderCard(
          <BlockOutlined />,
          styles.iconCircleDanger,
          strings.inPerson.hostLinkRequestVoidedTitle,
          strings.inPerson.hostLinkRequestVoidedSubtitle,
        );
      case strings.expired:
        return renderCard(
          <EventBusyOutlined />,
          styles.iconCircleDanger,
          strings.inPerson.hostLinkRequestExpiredTitle,
          strings.inPerson.hostLinkRequestExpiredSubtitle,
        );
      case strings.declined:
      case strings.rejected:
        return renderCard(
          <CancelOutlined />,
          styles.iconCircleDanger,
          strings.inPerson.hostLinkRequestDeclinedTitle,
          strings.inPerson.hostLinkRequestDeclinedSubtitle,
        );
      default:
        return null;
    }
  };

  /**
   * Nothing to hand off. The body is the server's own `waitingReason`, which
   * names the recipient and signing order being waited on — the generic string
   * is only a fallback for a backend that hasn't sent one.
   */
  const renderNotReady = () => (
    <Box sx={styles.overlay}>
      <Box sx={styles.card}>
        <Box sx={styles.iconCircleMuted}>
          <HourglassEmptyOutlined />
        </Box>
        <Typography component="h1" sx={styles.title}>
          {strings.inPerson.waitingOnCurrentLevelTitle}
        </Typography>
        <Typography sx={styles.subtitle}>
          {session?.waitingReason || strings.inPerson.noSignerReady}
        </Typography>
        {waitingOn.length > 0 && (
          <Box sx={styles.waitingBlock}>
            <Typography sx={styles.waitingLabel}>
              {strings.inPerson.waitingOnLabel}
            </Typography>
            <Box sx={styles.waitingChips}>
              {waitingOn.map((signer) => (
                <Typography
                  key={`${signer.signerId}-${signer.order}`}
                  sx={styles.waitingChip}
                >
                  {summaryLabel(signer)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  /** Only rendered when the server reports several signers actionable at once. */
  const renderPicker = () => (
    <Box sx={styles.pickerBlock}>
      <Typography sx={styles.pickerLabel}>
        {strings.inPerson.pickSignerLabel}
      </Typography>
      {actionableSigners.map((signer) => {
        const signerEmail = getDisplaySignerEmail(signer.signerEmail).trim();
        return (
          <ButtonBase
            disableRipple
            key={`${signer.signerId}-${signer.order}`}
            onClick={() => setSelectedRef(signer.signerRef ?? "")}
            sx={
              signer.signerRef && signer.signerRef === selectedRef
                ? styles.pickerOptionActive
                : styles.pickerOption
            }
          >
            <Typography sx={styles.pickerOptionName}>
              {summaryLabel(signer)}
            </Typography>
            <Typography sx={styles.pickerOptionMeta}>
              {signerEmail ||
                (signer.detailsRequired
                  ? strings.inPerson.pickSignerPlaceholder
                  : "")}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={styles.overlay}>
        <CircularProgress sx={{ color: theme.palette.common.white }} />
      </Box>
    );
  }

  if (loadError || !data) {
    return renderCard(
      <ErrorOutline />,
      styles.iconCircleMuted,
      strings.inPerson.hostLinkTitle,
      loadError || strings.inPerson.hostLinkLoadFailed,
    );
  }

  if (session?.sessionHeldByAnotherHost) {
    return renderCard(
      <GroupsOutlined />,
      styles.iconCircleMuted,
      strings.inPerson.hostLinkTitle,
      session.waitingReason || strings.inPerson.sessionInProgress,
    );
  }

  const terminalCard = getTerminalCard(session?.status);
  if (terminalCard) return terminalCard;

  // `canStartSession` is the single flag the button keys off — never a count
  // this page worked out for itself.
  if (!session?.canStartSession) return renderNotReady();

  return (
    <>
      {/* Closed the moment the capture dialog opens — the two stacked full-
          screen surfaces (this card underneath, the dialog on top) is what
          was cramping the dialog on mobile. Nothing else to show behind it
          once "Continue" is tapped, so there's nothing lost by hiding it. */}
      {!captureOpen && (
        <Box sx={styles.overlay}>
          <Box sx={styles.card}>
            <Box sx={styles.iconCircleOrange}>
              <TouchAppOutlined />
            </Box>
            <Typography component="h1" sx={styles.title}>
              {strings.inPerson.hostLinkReadyTitle}
            </Typography>
            <Typography sx={styles.subtitle}>
              {strings.inPerson.hostLinkReadySubtitle(
                data.requestedByName,
                data.emailSubject,
              )}
            </Typography>
            {needsChoice && renderPicker()}
            <Box sx={styles.actions}>
              {/* Pure hand-off to the capture dialog — no API call here at
                  all. That dialog is the one that actually starts the session. */}
              <Button
                disableRipple
                variant="contained"
                disabled={!canContinue}
                onClick={() => setCaptureOpen(true)}
                sx={styles.primaryButton}
              >
                {strings.inPerson.hostLinkStart}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <InPersonHandoffDialog
        open={captureOpen}
        requestId={data.requestId}
        hostToken={isTokenMode ? token : undefined}
        hostKey={isTokenMode ? key : undefined}
        message={captureMsg}
        // Pre-fills from the signer the server picked (or the host chose), so
        // a known recipient switched to in-person delivery isn't retyped. A
        // placeholder's generated `@no-email.invalid` address is masked — it
        // would otherwise be posted back as if the host had entered it.
        initialName={chosenSigner?.name?.trim() || undefined}
        initialEmail={
          getDisplaySignerEmail(chosenSigner?.signerEmail).trim() || undefined
        }
        loading={actionBusy}
        onCancel={() => setCaptureOpen(false)}
        onSubmit={handleCaptureSubmit}
      />
    </>
  );
};

export default InPersonHostLinkScreen;
